<template>
  <div class="ftr" :class="{ 'ftr--touch': touch }">
    <div v-for="bound in bounds" :key="bound.key" class="ftr__row">
      <label :for="fieldId(bound.key, 'datum')" class="ftr__bound">{{ bound.text }}</label>
      <div class="ftr__field">
        <app-icon name="calendar" :size="iconSize" class="ftr__icon" />
        <app-text-field
          :id="fieldId(bound.key, 'datum')"
          :value="draft[bound.key].date"
          autocomplete="off"
          placeholder="TT.MM.JJJJ"
          :aria-label="label + ' ' + bound.text + ', Datum'"
          :aria-describedby="errorBound === bound.key ? fieldId('fehler') : null"
          :error="errorBound === bound.key && errorPart === 'date'"
          @input="onInput(bound.key, 'date', $event)"
          @focus="openPopup(bound.key, 'date')"
          @click.native="openPopup(bound.key, 'date')"
          @blur="onBlur(bound.key, 'date')"
          @keydown.enter="commit(bound.key)"
          @keydown.esc="onEsc"
        />
      </div>
      <div class="ftr__field ftr__field--time">
        <app-icon name="clock" :size="iconSize" class="ftr__icon" />
        <app-text-field
          :id="fieldId(bound.key, 'zeit')"
          :value="draft[bound.key].time"
          autocomplete="off"
          inputmode="numeric"
          placeholder="hh:mm"
          :aria-label="label + ' ' + bound.text + ', Uhrzeit'"
          :aria-describedby="errorBound === bound.key ? fieldId('fehler') : null"
          :error="errorBound === bound.key && errorPart === 'time'"
          @input="onInput(bound.key, 'time', $event)"
          @focus="openPopup(bound.key, 'time')"
          @click.native="openPopup(bound.key, 'time')"
          @blur="onBlur(bound.key, 'time')"
          @keydown.enter="commit(bound.key)"
          @keydown.esc="onEsc"
        />
        <!-- Uhrzeiten zum Anklicken (halbe Stunden); der Fokus bleibt im Feld -->
        <div v-if="popup === bound.key + '-time'" ref="times" class="ftr__pop ftr__pop--time" @mousedown.prevent>
          <button
            v-for="time in times"
            :key="time"
            type="button"
            tabindex="-1"
            class="ftr__opt"
            :class="{ 'ftr__opt--current': currentTime(bound.key) === time }"
            @click="pickTime(bound.key, time)"
          >{{ time }}</button>
        </div>
      </div>
      <!-- Kalender unter der Zeile: ein Klick setzt nur dieses Feld -->
      <div v-if="popup === bound.key + '-date'" class="ftr__pop ftr__pop--cal" @mousedown.prevent>
        <v-date-picker
          :value="isoOf(bound.key)"
          no-title
          full-width
          locale="de"
          color="primary"
          :first-day-of-week="1"
          :picker-date.sync="pickerMonth"
          @input="pickDate(bound.key, $event)"
        ></v-date-picker>
      </div>
    </div>
    <p v-if="errorBound" :id="fieldId('fehler')" class="app-field__error ftr__error">
      <app-icon name="alert" :size="14" />{{ errorText }}
    </p>
  </div>
</template>

<script>
// Zeitauswahl eines Zeitpunkts im Filter-Panel (Kundenwunsch 14.10.2026; nach dem ersten Entwurf: „zu komplex …
// möglichst einfach und trotzdem vielfältig“, „datumsformat anzeigen, dass ich es auch manuell eingeben kann“, „einzeln
// von oder bis angeben … wenn ich einen tag anklicke, nicht von und bis ändern, sondern immer nur einen tag“). Zwei
// Zeilen von und bis mit je Datum und Uhrzeit; jede Seite geht allein (nur von = ab, nur bis = bis, gleiches Datum =
// dieser Tag). Platzhalter zeigen das Format. Hilfe nur am angeklickten Feld: Datum → Kalender unter der Zeile (ein Klick
// setzt nur dieses Feld und schließt ihn), Uhrzeit → Liste der halben Stunden. Getippt geht auch kurz: „13.10.“
// (laufendes Jahr), „524“ = 05:24, „17“ = 17:00; beim Verlassen steht der Wert ausgeschrieben da. Eine Uhrzeit ohne
// eigenes Datum nimmt das Datum der anderen Zeile.
// Zustand wie saleFilters.js: from/to als Text „13.10.2026 05:24“ bzw. „13.10.2026“ ('' = offen). Ereignis change
// ({ from, to }) nach Klick im Kalender oder in der Liste, beim Verlassen eines Felds, mit Enter und 400 ms nach gültiger
// Eingabe. pending() liefert noch nicht gemeldete Eingaben (das Panel übernimmt sie beim Schließen).
import moment from 'moment';
import {
  normalizeDate,
  normalizeTime,
  splitDateTime,
  joinDateTime,
  dateToIso,
  isoToDate
} from './saleFilters';

const BOUNDS = [{ key: 'from', text: 'von' }, { key: 'to', text: 'bis' }];
const TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  return (h < 10 ? '0' : '') + h + (i % 2 ? ':30' : ':00');
});
const LIVE_DELAY = 400;

export default {
  name: 'FilterTimeRange',
  props: {
    label: { type: String, required: true },
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    // Tage mit Einträgen ('YYYY-MM-DD'): der Kalender öffnet ohne Datum im Monat des letzten
    days: { type: Array, default: () => [] }
  },
  data() {
    return {
      bounds: BOUNDS,
      times: TIMES,
      draft: { from: { date: '', time: '' }, to: { date: '', time: '' } },
      errorBound: null,
      errorPart: null,
      errorText: '',
      // offene Hilfe: 'from-date', 'to-time' …
      popup: null,
      // Feld mit Fokus: währenddessen folgen die Felder nicht dem Zustand der Seite
      focused: null,
      pickerMonth: null
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    iconSize() {
      return this.touch ? 18 : 16;
    }
  },
  watch: {
    from() {
      this.syncIfIdle();
    },
    to() {
      this.syncIfIdle();
    }
  },
  created() {
    this.liveTimer = null;
    this.sync();
  },
  beforeDestroy() {
    clearTimeout(this.liveTimer);
  },
  methods: {
    fieldId(bound, part) {
      return 'ftr-' + this._uid + '-' + bound + (part ? '-' + part : '');
    },
    isoOf(bound) {
      return dateToIso(normalizeDate(this.draft[bound].date) || '') || null;
    },
    currentTime(bound) {
      return normalizeTime(this.draft[bound].time) || '';
    },
    syncIfIdle() {
      if (!this.focused && !this.liveTimer) {
        this.sync();
      }
    },
    sync() {
      this.draft = { from: splitDateTime(this.from), to: splitDateTime(this.to) };
      this.clearError();
    },
    clearError() {
      this.errorBound = null;
      this.errorPart = null;
      this.errorText = '';
    },
    openPopup(bound, part) {
      this.focused = bound + '-' + part;
      if (this.popup === bound + '-' + part) {
        return;
      }
      this.popup = bound + '-' + part;
      if (part === 'date') {
        // Monat des Felds, sonst der anderen Zeile, sonst des letzten Tags mit Einträgen, sonst der laufende
        const other = bound === 'from' ? 'to' : 'from';
        const iso = this.isoOf(bound) || this.isoOf(other) || this.days[this.days.length - 1] || moment().format('YYYY-MM-DD');
        this.pickerMonth = iso.slice(0, 7);
      } else {
        this.$nextTick(() => this.scrollTimes(bound));
      }
      // aufgeklappte Hilfe ins Bild rollen (Handy: das Panel scrollt, der Kalender ragte unten hinaus)
      this.$nextTick(() => {
        const pop = this.$el.querySelector('.ftr__pop');
        if (pop && pop.scrollIntoView) {
          pop.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    // Liste so rollen, dass die gewählte bzw. nächste halbe Stunde oben steht
    scrollTimes(bound) {
      const list = Array.isArray(this.$refs.times) ? this.$refs.times[0] : this.$refs.times;
      if (!list) {
        return;
      }
      const value = this.currentTime(bound);
      const index = value ? Math.min(TIMES.length - 1, Number(value.slice(0, 2)) * 2 + (Number(value.slice(3)) >= 30 ? 1 : 0)) : 0;
      const item = list.children[index];
      list.scrollTop = item ? item.offsetTop : 0;
    },
    onEsc(e) {
      if (this.popup) {
        e.stopPropagation();
        this.popup = null;
      }
    },
    onInput(bound, part, text) {
      this.draft[bound][part] = text;
      this.popup = null;
      this.clearError();
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => {
        this.liveTimer = null;
        if (!this.resolve(bound).error) {
          this.emitIfChanged(this.texts());
        }
      }, LIVE_DELAY);
    },
    onBlur(bound, part) {
      if (this.focused === bound + '-' + part) {
        this.focused = null;
      }
      if (this.popup === bound + '-' + part) {
        this.popup = null;
      }
      this.commit(bound);
    },
    // Eingabe einer Zeile lesen: { text, date, time } oder { error, part }
    resolve(bound) {
      const other = bound === 'from' ? 'to' : 'from';
      const date = normalizeDate(this.draft[bound].date);
      const time = normalizeTime(this.draft[bound].time);
      if (date === null) {
        return { error: 'Datum als TT.MM.JJJJ eingeben, z. B. 13.10.2026', part: 'date' };
      }
      if (time === null) {
        return { error: 'Uhrzeit als hh:mm eingeben, z. B. 05:24', part: 'time' };
      }
      let day = date;
      if (!day && time) {
        day = normalizeDate(this.draft[other].date) || '';
        if (!day) {
          return { error: 'Zur Uhrzeit bitte auch ein Datum angeben', part: 'date' };
        }
      }
      return { text: joinDateTime(day, time), date: day, time };
    },
    texts() {
      const from = this.resolve('from');
      const to = this.resolve('to');
      return { from: from.error ? this.from : from.text, to: to.error ? this.to : to.text };
    },
    emitIfChanged(texts) {
      if (texts.from !== this.from || texts.to !== this.to) {
        this.$emit('change', texts);
      }
    },
    // Zeile übernehmen: ausgeschrieben anzeigen, Fehler zeigen, Zustand melden
    commit(bound) {
      clearTimeout(this.liveTimer);
      this.liveTimer = null;
      const result = this.resolve(bound);
      if (result.error) {
        this.errorBound = bound;
        this.errorPart = result.part;
        this.errorText = result.error;
        return;
      }
      this.draft[bound] = { date: result.date, time: result.time };
      this.emitIfChanged(this.texts());
    },
    pickDate(bound, iso) {
      this.draft[bound].date = isoToDate(iso);
      this.popup = null;
      this.clearError();
      this.commit(bound);
    },
    pickTime(bound, time) {
      this.draft[bound].time = time;
      this.popup = null;
      this.clearError();
      this.commit(bound);
    },
    // noch nicht gemeldete gültige Eingaben (beim Schließen des Panels); null, wenn nichts offen ist
    pending() {
      clearTimeout(this.liveTimer);
      this.liveTimer = null;
      const texts = this.texts();
      return texts.from !== this.from || texts.to !== this.to ? texts : null;
    }
  }
};
</script>

<style scoped>
/* oben 8: Abstand zur Überschrift, und der Fokusrahmen der Felder (2 + 2 px) wird vom scrollenden Bereich nicht
   abgeschnitten (Kundenhinweis 14.10.2026) */
.ftr {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px 12px;
}
/* von | Datum | Uhrzeit; Hilfen hängen absolut an der Zeile bzw. am Uhrzeitfeld */
.ftr__row {
  position: relative;
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) 88px;
  align-items: center;
  gap: 8px;
}
.ftr--touch .ftr__row {
  grid-template-columns: 24px minmax(0, 1fr) 104px;
}
.ftr__bound {
  font-size: var(--fs-ctl);
  color: var(--ink-2);
}
.ftr__field {
  position: relative;
  min-width: 0;
}
.ftr__icon {
  position: absolute;
  top: 50%;
  left: 9px;
  z-index: 1;
  transform: translateY(-50%);
  color: var(--ink-3);
  pointer-events: none;
}
.ftr--touch .ftr__icon {
  left: 12px;
}
.ftr__field ::v-deep .app-input {
  padding-left: 31px;
}
.ftr--touch .ftr__field ::v-deep .app-input {
  padding-left: 38px;
}
.ftr__error {
  margin: 0;
}

/* ---------------------------------------------------------------- Hilfen (wie ein Menü) */
.ftr__pop {
  position: absolute;
  top: calc(100% + 4px);
  z-index: 5;
  box-sizing: border-box;
  background: var(--surface-raised);
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  box-shadow: var(--sh-menu);
}
.ftr__pop--cal {
  left: 0;
  right: 0;
  padding: 4px 6px 6px;
}
.ftr__pop--time {
  right: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 0;
}
.ftr__opt {
  display: block;
  width: 100%;
  height: var(--h-menu);
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  font-size: var(--fs-menu);
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
}
:where(html:not(.dichte-touch)) .ftr__opt:hover {
  background: var(--hov);
}
.ftr__opt:active {
  background: var(--press);
}
.ftr__opt--current {
  background: var(--sel);
  color: var(--link);
  font-weight: 600;
}

/* Kalender (Vuetify, Auswahl Theme primary) kompakt */
.ftr__pop--cal ::v-deep .v-picker,
.ftr__pop--cal ::v-deep .v-picker__body {
  background: transparent;
}
.ftr__pop--cal ::v-deep .v-date-picker-header {
  padding: 0 0 2px;
}
.ftr__pop--cal ::v-deep .v-date-picker-table {
  height: auto;
  padding: 0;
}
.ftr__pop--cal ::v-deep .v-date-picker-table--date td {
  padding: 1px 0;
}
.ftr__pop--cal ::v-deep .v-date-picker-table--date .v-btn {
  width: 30px;
  height: 30px;
}
.ftr--touch .ftr__pop--cal ::v-deep .v-date-picker-table--date .v-btn {
  width: 36px;
  height: 36px;
}
</style>
